// src/components/ReactFlow/ImageNode.tsx

import { useToast } from "@/hooks/use-toast";
import { ImageInNode, ImageType, Item } from "@/types/productTypes";
import { Handle, Position } from "@xyflow/react";
import { MouseEvent as ReactMouseEvent, useState } from "react";
import { useDrop } from "react-dnd";
import fetchDb from "../../../utils/customRequestFunction";
import { useAdminStore } from "../../../store/useAdminStore";
import { ImagePickerModal } from "./ImagePickerModal";
// If you have a constant for the DnD type:
const IMAGE_TYPE = "IMAGE";

interface ImageSlotProps {
   itemName: string;
   nodeId: number;
   itemId: number;
   image?: ImageInNode;
   onDropImage: (imageId: number) => void;
   removeImage: (imageId: number) => void;
   onClick: () => void;
}

function ImageSlot({
   itemName,
   image,
   onDropImage,
   removeImage,
   onClick,
}: ImageSlotProps) {
   const [{ isOver }, drop] = useDrop({
      accept: IMAGE_TYPE,
      drop: (draggedItem: { id: number }) => {
         onDropImage(draggedItem.id);
      },
      collect: (monitor) => ({
         isOver: monitor.isOver(),
      }),
   });

   return (
      <div className="flex flex-col items-center">
         <div className="text-[0.5rem] mb-1">{itemName}</div>
         <div
            ref={drop}
            onClick={onClick}
            className={`
                    w-[60px] h-[60px] rounded-md
                    flex items-center justify-center
                    cursor-pointer
                    transition-all
                    ${
                       isOver
                          ? "bg-blue-100 border-blue-400"
                          : "bg-gray-50 border-gray-200"
                    }
                    ${
                       image
                          ? "hover:ring-2 ring-blue-400"
                          : "border-2 border-dashed hover:border-blue-400"
                    }
                `}
         >
            {image ? (
               <div className="relative w-full h-full group">
                  <img
                     src={image.image.url}
                     alt={image.image.title || itemName}
                     className="w-full h-full object-cover rounded-md"
                  />
                  <button
                     onClick={(e) => {
                        e.stopPropagation();
                        removeImage(image.image.id);
                     }}
                     className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                  >
                     ×
                  </button>
               </div>
            ) : (
               <span className="text-xs text-gray-400">Drop or click</span>
            )}
         </div>
      </div>
   );
}

export interface ImagesNodeProps {
   id: string;
   data: {
      label: string;
      nodeId: number;
      imagesInNode: ImageInNode[];
      items: Item[];
      previousNode?: number;
      nextNode?: number;
      mainImage?: ImageInNode;
      optionPairId?: string;
   };
   selected?: boolean;
}

export function ImagesNode({ data, selected }: ImagesNodeProps) {
   const { label, imagesInNode, items, nodeId, optionPairId } = data;
   const { toast } = useToast();
   const [localImages, setLocalImages] = useState<ImageInNode[]>(imagesInNode);
   const [isPickerOpen, setIsPickerOpen] = useState(false);
   const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
   const { moveImage, setOptimisticNodeImages, refreshGraphData } =
      useAdminStore();
   const isRootNode = label === "root";

   console.log("ImageNode data for node:", nodeId, label);
   console.log("Is root node:", isRootNode);
   console.log("Main image from data:", data.mainImage);

   // Setup drop target for main image (only for root node)
   const [{ isMainImageOver }, mainImageDrop] = useDrop(
      () => ({
         accept: IMAGE_TYPE,
         drop: async (item: { id: number }) => {
            if (isRootNode) {
               try {
                  console.log("Image dropped on root node:", item.id);
                  // Fetch the image details
                  const response = await fetchDb.get(`/api/images/${item.id}`);
                  if (response.data) {
                     await handleSetMainImage(response.data);
                  }
               } catch (error) {
                  console.error("Error setting main image:", error);
                  toast({
                     title: "Error",
                     description: "Failed to set main image",
                     variant: "destructive",
                  });
               }
            }
         },
         collect: (monitor) => ({
            isMainImageOver: !!monitor.isOver(),
         }),
      }),
      [isRootNode, optionPairId]
   );

   const handleImageSelect = async (selectedImage: ImageType) => {
      try {
         // Create the new image-in-node relationship
         const newImageInNode: ImageInNode = {
            image: selectedImage,
            nodeId: nodeId,
            itemId: selectedItemId,
         };

         // Optimistically update the UI
         const updatedImages = [...localImages];
         const existingIndex = updatedImages.findIndex(
            (img) => img.itemId === selectedItemId
         );
         if (existingIndex !== -1) {
            updatedImages[existingIndex] = newImageInNode;
         } else {
            updatedImages.push(newImageInNode);
         }

         setLocalImages(updatedImages);
         setOptimisticNodeImages(nodeId, updatedImages);

         // Make the API call
         await fetchDb.patch("/api/graph/item-pair-image", {
            nodeId,
            imageId: selectedImage.id,
            itemId: selectedItemId,
         });

         toast({
            title: "Image Added",
            description: "Image successfully added to item",
            variant: "default",
         });

         return Promise.resolve();
      } catch (error) {
         // Revert optimistic update on error
         setLocalImages(imagesInNode);
         setOptimisticNodeImages(nodeId, imagesInNode);

         console.error("Error adding image:", error);
         toast({
            title: "Error",
            description: "Failed to add image",
            variant: "destructive",
         });
         return Promise.reject(error);
      }
   };

   const removeImageFromNode = async (imageId: number) => {
      try {
         // Optimistically update UI
         const updatedImages = localImages.filter(
            (img) => img.image.id !== imageId
         );
         setLocalImages(updatedImages);
         setOptimisticNodeImages(nodeId, updatedImages);

         // Make the API call
         await moveImage(imageId, -1, nodeId);
      } catch (error) {
         // Revert on error
         setLocalImages(imagesInNode);
         setOptimisticNodeImages(nodeId, imagesInNode);

         console.error("Error removing image:", error);
         toast({
            title: "Error",
            description: "Failed to remove image",
            variant: "destructive",
         });
      }
   };

   const handleSlotClick = (itemId: number) => {
      setSelectedItemId(itemId);
      setIsPickerOpen(true);
   };

   // Handle setting the main image for the root node
   const handleSetMainImage = async (image: any) => {
      try {
         console.log(
            `Setting main image for option pair ${optionPairId}:`,
            image
         );

         if (!optionPairId) {
            console.error("No optionPairId provided to root node");
            toast({
               title: "Error",
               description: "Missing option pair ID",
               variant: "destructive",
            });
            return;
         }

         await fetchDb.post(`/api/option-pair/${optionPairId}/main-image`, {
            imageId: image.id,
         });

         // Refresh the graph to get the updated data
         await refreshGraphData();

         toast({
            title: "Success",
            description: "Main image set successfully",
         });
      } catch (error) {
         console.error("Error setting main image:", error);
         toast({
            title: "Error",
            description: "Failed to set main image",
            variant: "destructive",
         });
      }
   };

   // Remove the main image
   const handleRemoveMainImage = async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
         await fetchDb.delete(`/api/option-pair/${optionPairId}/main-image`);

         // Refresh the graph to get the updated data
         await refreshGraphData();

         toast({
            title: "Success",
            description: "Main image removed successfully",
         });
      } catch (error) {
         console.error("Error removing main image:", error);
         toast({
            title: "Error",
            description: "Failed to remove main image",
            variant: "destructive",
         });
      }
   };

   return (
      <div
         className={`p-3 rounded-md border ${
            selected ? "border-blue-500" : "border-gray-300"
         } bg-white shadow-sm`}
      >
         {/* Node label */}
         <div className="font-semibold mb-2">{label}</div>

         {/* Main image for root node - styled like a regular image slot */}
         {isRootNode && (
            <div className="mb-3">
               <div className="flex flex-col items-center">
                  <div className="text-[0.5rem] mb-1">Main Image</div>
                  <div
                     ref={mainImageDrop}
                     onClick={() => {
                        if (!optionPairId) {
                           toast({
                              title: "Error",
                              description: "Missing option pair ID",
                              variant: "destructive",
                           });
                           return;
                        }
                        setIsPickerOpen(true);
                        setSelectedItemId(null);
                     }}
                     className={`
                                w-[60px] h-[60px] rounded-md
                                flex items-center justify-center
                                cursor-pointer
                                transition-all
                                ${
                                   isMainImageOver
                                      ? "bg-blue-100 border-blue-400"
                                      : ""
                                }
                                ${
                                   data.mainImage
                                      ? "hover:ring-2 ring-blue-400"
                                      : "border-2 border-dashed hover:border-blue-400 bg-gray-50 border-gray-200"
                                }
                            `}
                  >
                     {data.mainImage ? (
                        <div className="relative w-full h-full group">
                           <img
                              src={data.mainImage.url}
                              alt={data.mainImage.title || "Main Image"}
                              className="w-full h-full object-cover rounded-md"
                           />
                           <button
                              onClick={handleRemoveMainImage}
                              className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                           >
                              ×
                           </button>
                        </div>
                     ) : (
                        <span className="text-xs text-gray-400">
                           Drop or click
                        </span>
                     )}
                  </div>
               </div>
            </div>
         )}

         {/* Image slots grid - only show if there are items */}
         {items && items.length > 0 && (
            <div className="grid grid-cols-2 gap-2 p-2">
               {items.map((item) => (
                  <ImageSlot
                     key={item.id}
                     nodeId={nodeId}
                     itemId={item.id}
                     image={localImages.find((img) => img.itemId === item.id)}
                     itemName={item.name}
                     onDropImage={(imageId) =>
                        handleImageSelect({ id: imageId } as ImageType)
                     }
                     removeImage={removeImageFromNode}
                     onClick={() => handleSlotClick(item.id)}
                  />
               ))}
            </div>
         )}

         {/* Image picker modal */}
         <ImagePickerModal
            open={isPickerOpen}
            onClose={() => {
               setIsPickerOpen(false);
               setSelectedItemId(null);
            }}
            onSelect={
               selectedItemId === null
                  ? (image) => {
                       handleSetMainImage(image);
                       setIsPickerOpen(false);
                    }
                  : handleImageSelect
            }
            nodeName={label}
            itemName={
               selectedItemId === null
                  ? "Main Image"
                  : items?.find((item) => item.id === selectedItemId)?.name ||
                    "Item"
            }
         />

         {/* Handles for connections */}
         <Handle
            type="target"
            position={Position.Top}
            style={{ background: "#555" }}
         />
         <Handle
            type="source"
            position={Position.Bottom}
            style={{ background: "#555" }}
         />
      </div>
   );
}
